import React, { useState, useEffect } from "react";
import "./ReceiptPDFTemplate.scss";
import { getMyClientsByID } from "../../services/api/MyClientsApi";
import {
    getHighCasesByCourtRef,
    getDistrictCasesByCourtRef,
    getOtherCourtCasesAddedManuallyByCourtRef,
    getSupremeCourtCasesByCourtRef,
} from "../../services/api/CasesApi";

export const ReceiptPDFTemplate = ({ payment, invoice, settings }) => {
    const [clientDetails, setClientDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [caseDetails, setCaseDetails] = useState(null);

    const {
        headerText,
        header,
        footer,
        displayHeaderFooter,
        displayBankDetails,
        bankDetails,
        isSignatureDigitalGenerated,
        digitalSignature,
        gstNo,
        panNo,
        address,
    } = settings;

    // Fetch client details and case details when component mounts
    useEffect(() => {
        const fetchClientDetails = async () => {
            if (invoice?.issuedTo) {
                setLoading(true);
                try {
                    const response = await getMyClientsByID(invoice.issuedTo);
                    setClientDetails(response.data);
                } catch (error) {
                    console.error("Error fetching client details:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchClientDetails();
    }, [invoice?.issuedTo]);

    // Fetch case details to get caseTypeAndNumber
    useEffect(() => {
        const fetchCaseDetails = async () => {
            try {
                // Check which court type has the selected case
                let courtRef = null;
                let courtType = null;

                if (invoice?.highCourtCases?.length > 0) {
                    courtRef = invoice.highCourtCases[0];
                    courtType = 'high';
                } else if (invoice?.districtCourtCases?.length > 0) {
                    courtRef = invoice.districtCourtCases[0];
                    courtType = 'district';
                } else if (invoice?.otherCourtCases?.length > 0) {
                    courtRef = invoice.otherCourtCases[0];
                    courtType = 'other';
                } else if (invoice?.supremeCourtCases?.length > 0) {
                    courtRef = invoice.supremeCourtCases[0];
                    courtType = 'supreme';
                }

                if (!courtRef) return;

                // Fetch the case mappings based on court type
                let caseMappings = [];
                if (courtType === 'high') {
                    const response = await getHighCasesByCourtRef();
                    caseMappings = response.data || [];
                } else if (courtType === 'district') {
                    const response = await getDistrictCasesByCourtRef();
                    caseMappings = response.data || [];
                } else if (courtType === 'other') {
                    const response = await getOtherCourtCasesAddedManuallyByCourtRef();
                    caseMappings = response.data || [];
                } else if (courtType === 'supreme') {
                    const response = await getSupremeCourtCasesByCourtRef();
                    caseMappings = response.data || [];
                }

                // Find the case with matching courtRef
                const matchedCase = caseMappings.find(c => c.courtRef === courtRef);
                if (matchedCase) {
                    setCaseDetails({
                        courtRef: matchedCase.courtRef,
                        caseTypeAndNumber: matchedCase.caseTypeAndNumber,
                        parties: matchedCase.parties,
                        courtType: courtType
                    });
                }
            } catch (error) {
                console.error("Error fetching case details:", error);
            }
        };

        fetchCaseDetails();
    }, [invoice?.highCourtCases, invoice?.districtCourtCases, invoice?.otherCourtCases, invoice?.supremeCourtCases]);

    const formatAmount = (amount) => {
        return amount ? `₹${Number(amount).toFixed(2)}` : "₹0.00";
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="receipt-pdf">
            {displayHeaderFooter ? (
                header ? (
                    <img src={header} alt="Header" className="receipt-header" />
                ) : (
                    <p>Header not available</p>
                )
            ) : (
                <h2 className="company-name">{headerText}</h2>
            )}

            <div className="receipt-title">
                <h1>PAYMENT RECEIPT</h1>
            </div>

            <div className="receipt-details-container req-pad">
                <div className="receipt-info">
                    <p><strong>Receipt ID:</strong> {payment.recieptId || "N/A"}</p>
                    <p><strong>Receipt Date:</strong> {formatDateTime(payment.paymentDate)}</p>
                    <p><strong>Invoice No:</strong> {invoice?.invoiceNumber || "N/A"}</p>
                    <p><strong>Invoice Date:</strong> {invoice?.createdAt ? formatDate(invoice.createdAt) : "N/A"}</p>
                    {caseDetails?.caseTypeAndNumber && (
                        <p style={{ textDecoration: 'none' }}><strong style={{ textDecoration: 'none' }}>Associated Case:</strong> {caseDetails.caseTypeAndNumber}</p>
                    )}
                    {gstNo && <p><strong>GST No:</strong> {gstNo}</p>}
                    {panNo && <p><strong>PAN No:</strong> {panNo}</p>}
                    <p><strong>Address:</strong> {address}</p>
                </div>

                <div className="client-info">
                    {loading ? (
                        <p>Loading client details...</p>
                    ) : clientDetails ? (
                        <>
                            <p><strong>Name:</strong> {clientDetails.salutation} {clientDetails.name}</p>
                            <p><strong>Company:</strong> {clientDetails.company}</p>
                            {clientDetails.gstin && <p><strong>GST No:</strong> {clientDetails.gstin}</p>}
                            {clientDetails.addressLine1 && <p><strong>Address:</strong> {clientDetails.addressLine1}</p>}
                            {clientDetails.addressLine2 && <p>{clientDetails.addressLine2}</p>}
                            <p>{clientDetails.city}, {clientDetails.state} - {clientDetails.zipcode}</p>
                            <p>{clientDetails.country}</p>
                        </>
                    ) : (
                        <p>Client details not available</p>
                    )}
                </div>
            </div>

            <div className="payment-details-section req-pad">
                <h3>Payment Details</h3>
                <table className="payment-details-table">
                    <tbody>
                        <tr>
                            <td><strong>Payment Method:</strong></td>
                            <td>{payment.paymentType}</td>
                        </tr>
                        {payment.paymentType !== "OTHER" && (
                            <tr>
                                <td><strong>Amount Paid:</strong></td>
                                <td className="amount-cell">{formatAmount(payment.amount)}</td>
                            </tr>
                        )}
                        <tr>
                            <td><strong>Payment Date & Time:</strong></td>
                            <td>{formatDateTime(payment.paymentDate)}</td>
                        </tr>
                        {payment.refNumber && (
                            <tr>
                                <td><strong>Reference Number:</strong></td>
                                <td>{payment.refNumber}</td>
                            </tr>
                        )}
                        {payment.chequeNumber && (
                            <tr>
                                <td><strong>Cheque Number:</strong></td>
                                <td>{payment.chequeNumber}</td>
                            </tr>
                        )}
                        {payment.bankName && (
                            <tr>
                                <td><strong>Bank Name:</strong></td>
                                <td>{payment.bankName}</td>
                            </tr>
                        )}
                        {payment.remark && (
                            <tr>
                                <td><strong>Remarks:</strong></td>
                                <td>{payment.remark}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {payment.paymentType !== "OTHER" && (
                <div className="amount-in-words req-pad">
                    <p><strong>Amount in Words:</strong> {convertToWords(payment.amount)} Rupees Only</p>
                </div>
            )}

            {displayBankDetails && (
                <div className="bank-details req-pad">
                    <strong>Bank Details:</strong>
                    <p>{bankDetails}</p>
                </div>
            )}

            <div className="receipt-footer-section req-pad">
                <div className="signature">
                    {isSignatureDigitalGenerated ? (
                        <img src={digitalSignature} alt="Digital Signature" />
                    ) : (
                        <p>This is a system-generated receipt</p>
                    )}
                </div>
                
                <div className="generated-info">
                    <p>Generated on: {new Date().toLocaleString('en-IN')}</p>
                    <p>Thank you for your payment!</p>
                </div>
            </div>

            {displayHeaderFooter && footer && (
                <img src={footer} alt="Footer" className="receipt-footer" />
            )}
        </div>
    );
};

// Helper function to convert numbers to words (basic implementation)
function convertToWords(amount) {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    if (!amount || amount === 0) return 'Zero';
    
    const num = Math.floor(Number(amount));
    if (num === 0) return 'Zero';
    
    let result = '';
    
    // Crores
    if (num >= 10000000) {
        const crores = Math.floor(num / 10000000);
        result += convertHundreds(crores) + ' Crore ';
    }
    
    // Lakhs
    const lakhs = Math.floor((num % 10000000) / 100000);
    if (lakhs > 0) {
        result += convertHundreds(lakhs) + ' Lakh ';
    }
    
    // Thousands
    const thousands = Math.floor((num % 100000) / 1000);
    if (thousands > 0) {
        result += convertHundreds(thousands) + ' Thousand ';
    }
    
    // Hundreds
    const hundreds = num % 1000;
    if (hundreds > 0) {
        result += convertHundreds(hundreds);
    }
    
    return result.trim();
    
    function convertHundreds(n) {
        let str = '';
        if (n >= 100) {
            str += ones[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
        }
        if (n >= 20) {
            str += tens[Math.floor(n / 10)] + ' ';
            n %= 10;
        }
        if (n >= 10) {
            str += teens[n - 10] + ' ';
        } else if (n > 0) {
            str += ones[n] + ' ';
        }
        return str;
    }
}